/**
 * LearnEx — Personalized Learning Experience tab.
 *
 * Student view: numbered sub-topic checklist + locked "Take the LearnEx Quiz"
 * button. Once all ticks are filled, the button unlocks and the quiz modal
 * opens. Score posts back to the gradebook under category "learnex".
 *
 * Faculty view: same checklist but read-only ticks, plus a pencil button
 * that opens the Faculty editor modal (LearnExEditor below). Faculty can
 * AI-generate sub-topics + AI-generate the quiz from those sub-topics.
 */

import React, { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import useEffectiveRole from '../hooks/useEffectiveRole';

const NAVY = 'var(--gr-navy, #1A2B4C)';
const GOLD = 'var(--gr-gold, #FCD359)';

// ── Shimmer-wave animation injected once for the whole component tree ──────
const SHIMMER_STYLE = `
@keyframes learnex-wave {
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
}
.learnex-busy {
  position: relative !important;
  background-image: linear-gradient(
    110deg,
    rgba(252,211,89,0.35) 0%,
    rgba(252,211,89,0.95) 20%,
    rgba(255,253,249,0.85) 50%,
    rgba(252,211,89,0.95) 80%,
    rgba(252,211,89,0.35) 100%
  ) !important;
  background-size: 200% 100% !important;
  animation: learnex-wave 1.1s linear infinite !important;
  color: #1A2B4C !important;
  cursor: progress !important;
}
.learnex-busy::after {
  content: '';
  position: absolute; inset: 0;
  border-radius: inherit;
  box-shadow: 0 0 12px 2px rgba(252,211,89,0.6);
  pointer-events: none;
}
`;
const ensureShimmerStyleInjected = (() => {
  let injected = false;
  return () => {
    if (injected || typeof document === 'undefined') return;
    const id = 'learnex-shimmer-style';
    if (document.getElementById(id)) { injected = true; return; }
    const el = document.createElement('style');
    el.id = id; el.textContent = SHIMMER_STYLE;
    document.head.appendChild(el);
    injected = true;
  };
})();

// Reusable button — when `busy` true, the whole face waves like a shimmer.
// All async-triggering buttons in LearnEx use this so the working state
// is uniformly visible.
const BusyButton = ({ busy, disabled, className = '', style, children, ...rest }) => {
  ensureShimmerStyleInjected();
  const cls = `${className} ${busy ? 'learnex-busy' : ''}`.trim();
  return (
    <button className={cls} disabled={busy || disabled} style={style} {...rest}>
      {busy ? '…working…' : children}
    </button>
  );
};

// Authenticity badge — color graded green→amber→orange→red based on how much
// of the AI-generated Overview's concept-bigrams are grounded in the
// instructor's authored topic notes.
const AuthBadge = ({ score, size = 'sm' }) => {
  const s = Math.max(0, Math.min(100, Number(score || 0)));
  const palette =
    s >= 90 ? { bg: '#dcfce7', fg: '#166534', border: '#86efac', label: 'Faithful' }
  : s >= 75 ? { bg: '#fef9c3', fg: '#854d0e', border: '#fde047', label: 'Mostly faithful' }
  : s >= 50 ? { bg: '#fed7aa', fg: '#9a3412', border: '#fdba74', label: 'Partially faithful' }
  :           { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', label: 'Diverged' };
  const pad = size === 'lg' ? 'px-3 py-1.5 text-xs' : 'px-2 py-0.5 text-[10px]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-bold ${pad}`}
          style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}
          data-testid="learnex-authenticity-badge"
          title={`${palette.label} — ${s}% of this Overview's concept-bigrams appear in the instructor's authored topic notes.`}>
      {s.toFixed(0)}% authentic
    </span>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Faculty editor modal — AI-generate + curate sub-topics and the quiz
// ──────────────────────────────────────────────────────────────────────────
const LearnExEditor = ({ moduleId, topicId, onClose, onSaved }) => {
  const [tab, setTab] = useState('subtopics'); // 'subtopics' | 'quiz'
  const [subtopics, setSubtopics] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [approved, setApproved] = useState(null); // {by, at}
  const [dirty, setDirty] = useState(false);
  const [advise, setAdvise] = useState(null);   // {sid, score, reasoning, supplement_markdown}
  const [adviseBusyId, setAdviseBusyId] = useState(null);
  const [tightenBusyId, setTightenBusyId] = useState(null);

  const tightenOne = async (idx) => {
    const st = subtopics[idx];
    if (!st) return;
    setTightenBusyId(st.id); setErr('');
    try {
      const r = await api.post(
        `/api/topics/${moduleId}/${topicId}/learnex/subtopics:tighten`,
        { title: st.title, content: st.content || '' });
      const copy = [...subtopics];
      copy[idx] = {
        ...copy[idx],
        content: r.data.content,
        authenticity_score: r.data.authenticity_score,
        judge_reasoning: r.data.judge_reasoning,
      };
      setSubtopics(copy);
      setDirty(true);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Tighten failed.');
    } finally { setTightenBusyId(null); }
  };


  const openAdvise = async (st) => {
    setAdviseBusyId(st.id); setErr('');
    try {
      const r = await api.post(
        `/api/topics/${moduleId}/${topicId}/learnex/subtopics/${st.id}/advise`,
        {
          title: st.title,
          content: st.content || '',
          judge_reasoning: st.judge_reasoning || '',
          authenticity_score: st.authenticity_score || 0,
        });
      setAdvise({
        sid: st.id, title: st.title, score: r.data?.score,
        reasoning: r.data?.reasoning, supplement_markdown: r.data?.supplement_markdown,
      });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Advise failed.');
    } finally { setAdviseBusyId(null); }
  };

  // Load existing
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/api/topics/${moduleId}/${topicId}/learnex/subtopics`);
        if (!cancel) {
          setSubtopics(r.data?.subtopics || []);
          setApproved(r.data?.approved_by
            ? { by: r.data.approved_by, at: r.data.approved_at } : null);
        }
      } catch (_) { /* empty is OK */ }
      try {
        const q = await api.get(`/api/topics/${moduleId}/${topicId}/learnex/quiz`);
        if (!cancel) setQuiz(q.data?.questions || []);
      } catch (_) { /* empty is OK */ }
    })();
    return () => { cancel = true; };
  }, [moduleId, topicId]);

  const markDirty = (setter) => (next) => { setter(next); setDirty(true); };

  const genSubtopics = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/api/topics/${moduleId}/${topicId}/learnex/subtopics:generate`);
      const generated = r.data?.subtopics || [];
      setSubtopics(generated);
      // Immediately fire the parallel-judge scorer so faculty see real
      // authenticity numbers without having to click Save first. The
      // backend splits these into two endpoints to stay under the 60s
      // ingress timeout (generate ~30-50s, score ~5-10s).
      if (generated.length) {
        try {
          const s = await api.post(
            `/api/topics/${moduleId}/${topicId}/learnex/subtopics:score`,
            { subtopics: generated });
          const scoreMap = {};
          (s.data?.scores || []).forEach(x => { scoreMap[x.id] = x; });
          setSubtopics(generated.map(st => ({
            ...st,
            authenticity_score: scoreMap[st.id]?.authenticity_score ?? st.authenticity_score,
            judge_reasoning: scoreMap[st.id]?.judge_reasoning ?? st.judge_reasoning,
          })));
        } catch (se) {
          // Scoring failure is non-fatal — sub-topics are still usable.
          // eslint-disable-next-line no-console
          console.warn('Authenticity scoring failed:', se);
        }
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'AI generation failed.');
    } finally { setBusy(false); }
  };

  const rescoreSubtopics = async () => {
    if (!subtopics.length) return;
    setBusy(true); setErr('');
    try {
      const s = await api.post(
        `/api/topics/${moduleId}/${topicId}/learnex/subtopics:score`,
        { subtopics });
      const scoreMap = {};
      (s.data?.scores || []).forEach(x => { scoreMap[x.id] = x; });
      setSubtopics(subtopics.map(st => ({
        ...st,
        authenticity_score: scoreMap[st.id]?.authenticity_score ?? st.authenticity_score,
        judge_reasoning: scoreMap[st.id]?.judge_reasoning ?? st.judge_reasoning,
      })));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Re-score failed.');
    } finally { setBusy(false); }
  };

  const saveSubtopics = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.put(`/api/topics/${moduleId}/${topicId}/learnex/subtopics`, { subtopics });
      // Server refreshes authenticity scores on save; reflect them locally.
      if (r.data?.subtopics) setSubtopics(r.data.subtopics);
      setApproved(null);  // any save invalidates prior sign-off
      setDirty(false);
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const approveSubtopics = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/api/topics/${moduleId}/${topicId}/learnex/subtopics/approve`);
      setApproved({ by: r.data?.approved_by, at: r.data?.approved_at });
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Approve failed.');
    } finally { setBusy(false); }
  };

  const genQuiz = async () => {
    setBusy(true); setErr('');
    try {
      const r = await api.post(`/api/topics/${moduleId}/${topicId}/learnex/quiz:generate`);
      setQuiz(r.data?.questions || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'AI generation failed.');
    } finally { setBusy(false); }
  };

  const saveQuiz = async () => {
    setBusy(true); setErr('');
    try {
      await api.put(`/api/topics/${moduleId}/${topicId}/learnex/quiz`, { questions: quiz });
      onSaved && onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="learnex-editor">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b"
             style={{ background: NAVY, color: GOLD }}>
          <div className="font-bold">LearnEx — Faculty Editor</div>
          <button onClick={onClose} className="text-sm"
                  data-testid="learnex-editor-close">✕ Close</button>
        </div>

        <div className="flex gap-2 px-5 py-2 border-b">
          <button onClick={() => setTab('subtopics')}
                  className="px-3 py-1.5 rounded text-sm font-bold border transition-all"
                  style={{
                    background: tab === 'subtopics' ? GOLD : 'white',
                    color: tab === 'subtopics' ? NAVY : '#6b7280',
                    borderColor: tab === 'subtopics' ? GOLD : '#e5e7eb',
                  }}
                  data-testid="learnex-editor-tab-subtopics">
            1. Sub-topics ({subtopics.length})
          </button>
          <button onClick={() => setTab('quiz')}
                  className="px-3 py-1.5 rounded text-sm font-bold border transition-all"
                  style={{
                    background: tab === 'quiz' ? GOLD : 'white',
                    color: tab === 'quiz' ? NAVY : '#6b7280',
                    borderColor: tab === 'quiz' ? GOLD : '#e5e7eb',
                  }}
                  data-testid="learnex-editor-tab-quiz">
            2. Quiz ({quiz.length})
          </button>
        </div>

        {err && <div className="bg-red-50 text-red-700 px-5 py-2 text-sm">{err}</div>}

        {tab === 'subtopics' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <BusyButton busy={busy} onClick={genSubtopics}
                      className="btn-outline text-sm font-bold"
                      style={{ background: GOLD, color: NAVY }}
                      data-testid="learnex-editor-generate-subtopics-btn">
                ✨ AI-generate sub-topics
              </BusyButton>
              <BusyButton busy={busy} disabled={subtopics.length === 0}
                      onClick={saveSubtopics}
                      className="btn-outline text-sm"
                      data-testid="learnex-editor-save-subtopics-btn">
                💾 Save
              </BusyButton>
              <BusyButton busy={busy} disabled={subtopics.length === 0}
                      onClick={rescoreSubtopics}
                      className="btn-outline text-sm"
                      title="Re-run the LLM-as-judge authenticity scorer on the current sub-topics in this editor. No save required — useful after hand-editing study notes to see the new score before approving."
                      data-testid="learnex-editor-rescore-subtopics-btn">
                🔄 Re-score all
              </BusyButton>
              {approved && !dirty && (
                <span className="ml-auto text-xs text-green-700 font-bold inline-flex items-center gap-1"
                      data-testid="learnex-editor-approved-status">
                  ✓ Approved by {approved.by}
                </span>
              )}
            </div>
            <ol className="space-y-2">
              {subtopics.map((st, i) => (
                <li key={st.id} className="border rounded p-3" data-testid={`learnex-editor-subtopic-${i}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <input
                      type="text"
                      value={st.title}
                      onChange={(e) => {
                        const copy = [...subtopics]; copy[i].title = e.target.value;
                        setSubtopics(copy); setDirty(true);
                      }}
                      className="flex-1 font-bold border-b pb-1"
                      placeholder="Sub-topic title"
                    />
                    <AuthBadge score={st.authenticity_score} />
                    {st.authenticity_score < 90 && (
                      <BusyButton
                        busy={tightenBusyId === st.id}
                        onClick={() => tightenOne(i)}
                        className="px-2 py-1 rounded-full text-[10px] font-extrabold"
                        style={{
                          background: GOLD, color: NAVY, border: `1px solid ${NAVY}`,
                          letterSpacing: '0.05em',
                        }}
                        data-testid={`learnex-editor-tighten-btn-${i}`}
                        title="Rewrite this sub-topic to be ≥90% faithful to the source — may shorten it.">
                        🪡 Tighten
                      </BusyButton>
                    )}
                    {st.authenticity_score < 90 && (
                      <BusyButton
                        busy={adviseBusyId === st.id}
                        onClick={() => openAdvise(st)}
                        className="px-2 py-1 rounded-full text-[10px] font-extrabold"
                        style={{
                          background: NAVY, color: GOLD, border: `1px solid ${GOLD}`,
                          letterSpacing: '0.05em',
                        }}
                        data-testid={`learnex-editor-advise-btn-${i}`}
                        title="Get faculty advice on how to raise this sub-topic's authenticity score.">
                        ⚡ Advise
                      </BusyButton>
                    )}
                  </div>
                  <textarea
                    value={st.description}
                    onChange={(e) => {
                      const copy = [...subtopics]; copy[i].description = e.target.value;
                      setSubtopics(copy); setDirty(true);
                    }}
                    className="w-full text-sm text-gray-700 border-b mb-2 pb-1"
                    rows={2}
                    placeholder="One-sentence description"
                  />
                  <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">
                    Sub-topic Study Notes
                  </div>
                  <textarea
                    value={st.content || ''}
                    onChange={(e) => {
                      const copy = [...subtopics]; copy[i].content = e.target.value;
                      setSubtopics(copy); setDirty(true);
                    }}
                    className="w-full text-sm text-gray-800 border rounded p-2"
                    style={{ borderColor: 'var(--gr-border, #e5e7eb)' }}
                    rows={7}
                    placeholder="3 to 4 paragraphs (250–400 words) of focused Sub-topic Study Notes that this sub-topic teaches. Used as the in-place review material for the student before they tick Reviewed. Re-save to refresh the LLM-as-judge authenticity score against the full topic content."
                  />
                  <button onClick={() => { setSubtopics(subtopics.filter((_, j) => j !== i)); setDirty(true); }}
                          className="text-xs text-red-600 mt-2">Remove</button>
                </li>
              ))}
            </ol>
            <button onClick={() => {
                setSubtopics([...subtopics,
                  { id: `st_new_${Date.now()}`, title: '', description: '', content: '',
                    authenticity_score: 0, order: subtopics.length + 1 }]);
                setDirty(true);
              }}
                    className="text-sm text-gray-600 underline">
              + Add sub-topic
            </button>

            {/* Faculty sign-off bar (Item 1) */}
            <div className="border-t pt-3 mt-4">
              {approved && !dirty ? (
                <div className="flex items-center gap-2 text-sm" data-testid="learnex-signoff-banner">
                  <span className="px-2 py-1 rounded text-xs font-bold"
                        style={{ background: '#dcfce7', color: '#166534' }}>
                    ✓ Signed off
                  </span>
                  <span className="text-gray-600">
                    Approved by <strong>{approved.by}</strong>
                    {approved.at && ` on ${new Date(approved.at).toLocaleString()}`}
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="flex-1 text-xs text-gray-600">
                    Sign-off confirms you (faculty) have reviewed every AI-generated
                    Sub-topic Study Note for grounding faithfulness to your topic content.
                    {dirty && <span className="ml-1 text-amber-700 font-bold">
                      You have unsaved edits — save first, then sign off.
                    </span>}
                  </div>
                  <BusyButton busy={busy} disabled={subtopics.length === 0 || dirty}
                          onClick={approveSubtopics}
                          className="btn-outline text-sm font-bold disabled:opacity-50"
                          style={{
                            background: dirty ? '#e5e7eb' : GOLD,
                            color: NAVY,
                          }}
                          data-testid="learnex-editor-approve-btn">
                    ✓ Approve &amp; Sign Off
                  </BusyButton>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'quiz' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <BusyButton busy={busy} disabled={subtopics.length === 0}
                      onClick={genQuiz}
                      className="btn-outline text-sm font-bold"
                      style={{ background: GOLD, color: NAVY }}
                      data-testid="learnex-editor-generate-quiz-btn"
                      title={subtopics.length === 0 ? 'Save sub-topics first.' : ''}>
                ✨ AI-generate quiz
              </BusyButton>
              <BusyButton busy={busy} disabled={quiz.length === 0}
                      onClick={saveQuiz}
                      className="btn-outline text-sm"
                      data-testid="learnex-editor-save-quiz-btn">
                💾 Save
              </BusyButton>
            </div>
            {quiz.map((q, i) => (
              <div key={q.id} className="border rounded p-3 space-y-2"
                   data-testid={`learnex-editor-q-${i}`}>
                <div className="text-xs font-bold text-gray-500">Q{i + 1}</div>
                <div className="font-semibold">{q.prompt}</div>
                <ul className="space-y-1 text-sm">
                  {q.options.map((opt, j) => (
                    <li key={j} className={j === q.correct_index ? 'font-bold' : ''}>
                      {String.fromCharCode(65 + j)}. {opt}
                      {j === q.correct_index && <span className="ml-2 text-green-700">✓ correct</span>}
                    </li>
                  ))}
                </ul>
                {q.explanation && (
                  <div className="text-xs text-gray-600 italic">
                    Explanation: {q.explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Advise modal — surfaces judge reasoning + ready-to-paste supplement */}
      {advise && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
             data-testid="learnex-advise-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b"
                 style={{ background: NAVY, color: GOLD }}>
              <div className="font-bold">
                Advise &nbsp;·&nbsp; {advise.title}
                <span className="ml-3 text-xs font-normal opacity-90">
                  current score: {advise.score?.toFixed(0)}%
                </span>
              </div>
              <button onClick={() => setAdvise(null)} className="text-sm"
                      data-testid="learnex-advise-close">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">
                  Why the score is below 90
                </div>
                <div className="text-sm text-gray-800 bg-amber-50 border-l-4 border-amber-400 p-3 rounded"
                     data-testid="learnex-advise-reasoning">
                  {advise.reasoning || 'No specific gap identified.'}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-gray-500">
                    Supplement to paste into the topic
                  </div>
                  <button
                    onClick={() => navigator.clipboard?.writeText(advise.supplement_markdown || '')}
                    className="text-xs underline text-gray-600"
                    data-testid="learnex-advise-copy">
                    Copy
                  </button>
                </div>
                <textarea
                  readOnly
                  value={advise.supplement_markdown || '(none generated)'}
                  rows={12}
                  className="w-full text-sm text-gray-800 border rounded p-3 bg-gray-50"
                  data-testid="learnex-advise-supplement"
                />
                <div className="text-xs text-gray-500 mt-2 italic">
                  Click "✓ Apply" to append this directly to the topic Summary
                  and re-score this sub-topic, or copy/paste manually.
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={() => setAdvise(null)}
                    className="btn-outline text-sm"
                    data-testid="learnex-advise-cancel">
                    Cancel
                  </button>
                  <BusyButton
                    busy={applyBusy}
                    onClick={applyAdviseToTopic}
                    className="text-sm font-bold px-4 py-2 rounded-md"
                    style={{ background: '#166534', color: '#FFFFFF' }}
                    data-testid="learnex-advise-apply">
                    ✓ Apply to Topic &amp; Re-score
                  </BusyButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Quiz attempt modal
// ──────────────────────────────────────────────────────────────────────────
const QuizModal = ({ moduleId, topicId, onClose, onSubmitted }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/topics/${moduleId}/${topicId}/learnex/quiz`)
      .then((r) => setQuestions(r.data?.questions || []))
      .catch(() => setQuestions([]));
  }, [moduleId, topicId]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/topics/${moduleId}/${topicId}/learnex/quiz/attempts`,
        { answers });
      setResult(r.data);
      onSubmitted && onSubmitted(r.data);
      // Mastery moment — confetti when the student hits ≥80% on this run.
      // Dynamic import keeps the bundle lean; the burst is small (~80
      // particles) and brief.
      if (r.data?.passed) {
        try {
          const confetti = (await import('canvas-confetti')).default;
          const fire = () => confetti({
            particleCount: 80,
            spread: 70,
            origin: { y: 0.65 },
            colors: ['#FCD359', '#1A2B4C', '#22c55e', '#FFFFFF'],
            disableForReducedMotion: true,
          });
          fire();
          setTimeout(fire, 250);
        } catch (_) { /* confetti is non-fatal */ }
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Submit failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         data-testid="learnex-quiz-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b"
             style={{ background: NAVY, color: GOLD }}>
          <div className="font-bold">LearnEx Quiz</div>
          <button onClick={onClose} className="text-sm"
                  data-testid="learnex-quiz-modal-close">✕</button>
        </div>

        {result ? (
          <div className="p-5 space-y-4" data-testid="learnex-quiz-result">
            {/* Score + pass/retake banner */}
            <div className="text-center py-4">
              <div className="text-5xl font-bold"
                   style={{ color: result.passed ? '#166534' : NAVY }}
                   data-testid="learnex-quiz-result-score">
                {result.score_pct}%
              </div>
              <div className="text-gray-600 mt-1">
                {result.correct} / {result.total} correct
                {result.attempt_number && (
                  <span className="text-xs text-gray-400 ml-2">
                    · attempt #{result.attempt_number}
                  </span>
                )}
              </div>
              {result.passed ? (
                <div className="mt-3 inline-flex flex-col items-center gap-1 px-4 py-2 rounded-lg font-bold"
                     style={{ background: '#dcfce7', color: '#166534' }}
                     data-testid="learnex-quiz-passed-banner">
                  <div className="inline-flex items-center gap-2">
                    ✓ You met the {result.pass_threshold || 80}% mastery bar
                    {result.is_new_best && <span className="text-amber-700 ml-1">★ new best</span>}
                  </div>
                  {result.attempt_number > 1 && (
                    <div className="text-xs font-normal text-green-800/80">
                      You got there in {result.attempt_number} attempts — nice persistence!
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold"
                     style={{ background: 'rgba(252,211,89,0.30)', color: NAVY }}
                     data-testid="learnex-quiz-retake-banner">
                  Below {result.pass_threshold || 80}% — practice attempt, not yet saved
                </div>
              )}
            </div>

            {/* Per-question feedback — SHOWS ONLY the student's choice +
                a one-sentence "why your answer doesn't fit". The correct
                option is intentionally NOT revealed; students retake
                until they reach the mastery bar. */}
            <div className="space-y-3">
              {result.per_question.map((pq, i) => {
                const q = questions[i] || {};
                const selectedText =
                  (q.options || [])[pq.selected_index] ||
                  '(no option selected)';
                return (
                  <div key={pq.question_id}
                       className="border rounded p-3"
                       style={{
                         borderColor: pq.is_correct ? '#22c55e' : '#fca5a5',
                         background: pq.is_correct
                           ? 'rgba(34,197,94,0.06)'
                           : 'rgba(252,165,165,0.06)',
                       }}
                       data-testid={`learnex-quiz-result-q-${i}`}>
                    <div className="text-xs font-bold mb-1">
                      Q{i + 1} —{' '}
                      {pq.is_correct ? (
                        <span className="text-green-700">Correct</span>
                      ) : (
                        <span className="text-red-700">Not yet</span>
                      )}
                    </div>
                    <div className="text-sm font-semibold mb-2">{q.prompt}</div>
                    <div className="text-xs text-gray-700">
                      <span className="font-bold">Your answer:</span>{' '}
                      {selectedText}
                    </div>
                    {pq.feedback && (
                      <div className="text-xs mt-2 italic"
                           style={{
                             color: pq.is_correct ? '#166534' : '#7f1d1d',
                           }}
                           data-testid={`learnex-quiz-feedback-${i}`}>
                        💡 {pq.feedback}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions — retake until passed; once passed the score is
                already in the gradebook so the only button is Close. */}
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={onClose} className="btn-outline text-sm"
                      data-testid="learnex-quiz-result-close-btn">
                {result.passed ? 'Done' : 'Close (save progress later)'}
              </button>
              {!result.passed && (
                <button onClick={() => { setResult(null); setAnswers({}); }}
                        className="btn-outline text-sm font-bold flex-1"
                        style={{ background: GOLD, color: NAVY }}
                        data-testid="learnex-quiz-retake-btn">
                  🔁 Retake — keep practising
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {questions.map((q, i) => (
              <div key={q.id} data-testid={`learnex-quiz-q-${i}`}>
                <div className="font-semibold mb-2">
                  <span className="text-xs text-gray-500 mr-2">Q{i + 1}.</span>
                  {q.prompt}
                </div>
                <div className="space-y-1">
                  {q.options.map((opt, j) => (
                    <label key={j} className="flex items-start gap-2 cursor-pointer
                      hover:bg-gray-50 rounded p-1.5"
                      data-testid={`learnex-quiz-q-${i}-opt-${j}`}>
                      <input
                        type="radio"
                        name={q.id}
                        value={j}
                        checked={answers[q.id] === j}
                        onChange={() => setAnswers({ ...answers, [q.id]: j })}
                        className="mt-1"
                      />
                      <span className="text-sm">
                        {String.fromCharCode(65 + j)}. {opt}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <BusyButton
              busy={busy}
              disabled={Object.keys(answers).length !== questions.length}
              onClick={submit}
              className="btn-outline text-sm font-bold w-full disabled:opacity-50"
              style={{ background: GOLD, color: NAVY }}
              data-testid="learnex-quiz-submit-btn">
              Submit answers
            </BusyButton>
          </div>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main exported tab — NEW 2026-06-11 flow
//
// Student path is now: tick "I reviewed the Presentation" + "I reviewed
// the Video" → use the always-present Ask-the-Tutor ChatPanel for any
// clarification → click "I am ready to take the quiz" → quiz unlocks.
//
// The old AI sub-topic + authenticity flow is PARKED. The faculty
// editor still uses it in full (see PARKED_AI_AUTHENTICITY.md).
// ──────────────────────────────────────────────────────────────────────────
const LearnExTab = ({ moduleId, topicId, topicTitle }) => {
  // `useEffectiveRole` downgrades a developer/faculty viewing via
  // `/preview-as-student/...` to a plain student so the Faculty Editor
  // and other faculty-only affordances hide and the preview is honest.
  // It also downgrades a developer in `/preview-as-faculty/...` to a
  // teacher (no developer-only buttons leak into the faculty preview).
  const { isFaculty, isDeveloper } = useEffectiveRole();

  const [state, setState] = useState({
    items: [], ticked_item_ids: [], all_required_ticked: false,
    ready_for_quiz: false, has_quiz: false,
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [err, setErr] = useState('');

  // `initialLoad` gates the spinner the very first time the tab opens.
  // Subsequent silent refreshes (after tick/save/quiz-submit) must NOT
  // toggle it — otherwise the parent re-renders the loading branch,
  // unmounting any child modals (e.g. QuizModal) and losing their state.
  const refresh = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/api/topics/${moduleId}/${topicId}/learnex/review-items`);
      setState(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load LearnEx.');
    } finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [moduleId, topicId]);

  const tickItem = async (itemId, ticked) => {
    setBusy(true); setErr('');
    try {
      await api.post(
        `/api/topics/${moduleId}/${topicId}/learnex/review-items/${itemId}/tick`,
        { ticked });
      await refresh({ silent: true });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Tick failed.');
    } finally { setBusy(false); }
  };

  const confirmReady = async (ready) => {
    setBusy(true); setErr('');
    try {
      await api.post(
        `/api/topics/${moduleId}/${topicId}/learnex/review-items/ready`,
        { ready });
      await refresh({ silent: true });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Confirm failed.');
    } finally { setBusy(false); }
  };

  // Nudge the floating ChatPanel into focus + seed an opening question.
  const focusTutor = (kind, label) => {
    const opening =
      kind === 'presentation'
        ? `I just finished reviewing the Presentation for "${topicTitle}". ` +
          `Help me check my understanding — can you ask me one focused, ` +
          `concept-level question I should be able to answer from the slides?`
        : `I just finished watching the Video for "${topicTitle}". ` +
          `Help me check my understanding — can you ask me one focused, ` +
          `concept-level question I should be able to answer from the lecture?`;
    window.dispatchEvent(new CustomEvent('learnex:askTutor', {
      detail: {
        subtopic_title: label,
        subtopic_description: `Self-check on the ${label.toLowerCase()}.`,
        opening_prompt: opening,
      },
    }));
  };

  if (loading) return <div className="content-frame p-6 text-gray-500">Loading…</div>;

  const items = state.items || [];
  const empty = items.length === 0;

  return (
    <div className="content-frame p-6" data-testid="learnex-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold" style={{ color: NAVY }}>
            Personalized Learning Experience
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Review each faculty-published item below. Use the per-item{' '}
            <b>💬 Ask the AI Tutor</b> button for focused Q&amp;A on that
            material. When you're confident, confirm{' '}
            <i>"I am ready to take the quiz"</i> to unlock the assessment.
          </p>
        </div>
        {isFaculty && (
          <button onClick={() => setShowEditor(true)}
                  className="btn-outline text-sm font-bold"
                  style={{ background: GOLD, color: NAVY }}
                  data-testid="learnex-edit-btn"
                  title="Faculty only — open the AI sub-topic editor (parked workstream).">
            ✎ Faculty Editor
          </button>
        )}
      </div>

      {err && (
        <div className="bg-red-50 text-red-700 px-3 py-2 text-sm rounded mb-3"
             data-testid="learnex-error">{err}</div>
      )}

      {empty ? (
        <div className="text-gray-500 italic py-6 text-center" data-testid="learnex-empty">
          Your instructor has not yet attached a Presentation or Video to this topic.
        </div>
      ) : (
        <>
          <ol className="space-y-3 mb-5" data-testid="learnex-review-list">
            {items.map((it, i) => {
              const checked = state.ticked_item_ids.includes(it.id);
              return (
                <li key={it.id}
                    className="rounded-lg border p-4 transition-colors"
                    style={{
                      background: checked ? 'rgba(252,211,89,0.10)' : 'white',
                      borderColor: checked ? GOLD : 'var(--gr-border, #e5e7eb)',
                    }}
                    data-testid={`learnex-review-row-${it.id}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isFaculty || busy}
                      onChange={() => tickItem(it.id, !checked)}
                      className="mt-1 w-5 h-5 cursor-pointer"
                      data-testid={`learnex-review-tick-${it.id}`}
                      title="I have reviewed this item."
                    />
                    <div className="flex-1">
                      <div className="font-semibold flex items-center gap-2 flex-wrap"
                           style={{ color: NAVY }}>
                        <span>{i + 1}. {it.title}</span>
                        {it.required && (
                          <span className="text-[10px] uppercase tracking-wider
                                           font-bold px-1.5 py-0.5 rounded"
                                style={{ background: NAVY, color: GOLD }}>
                            Required
                          </span>
                        )}
                        {checked && (
                          <span className="text-xs text-amber-700 font-bold ml-1"
                                data-testid={`learnex-review-checked-${it.id}`}>
                            ✓ Reviewed
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {it.url && (
                          <a
                            href={it.url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn-outline text-xs"
                            style={{ background: NAVY, color: GOLD, borderColor: NAVY }}
                            data-testid={`learnex-review-open-${it.id}`}>
                            {it.kind === 'video' ? '▶ Open video' : '📑 Open slides'} ↗
                          </a>
                        )}
                        <button
                          onClick={() => focusTutor(it.kind, it.title)}
                          className="btn-outline text-xs"
                          data-testid={`learnex-review-ask-${it.id}`}
                          title="Open a focused Q&A with the AI Tutor on this material.">
                          💬 Ask the AI Tutor
                        </button>
                        {!isFaculty && !checked && (
                          <button
                            onClick={() => tickItem(it.id, true)}
                            disabled={busy}
                            className="btn-outline text-xs font-bold"
                            style={{ background: GOLD, color: NAVY }}
                            data-testid={`learnex-review-mark-${it.id}`}>
                            ✓ Mark as Reviewed
                          </button>
                        )}
                        {!isFaculty && checked && (
                          <button
                            onClick={() => tickItem(it.id, false)}
                            disabled={busy}
                            className="btn-outline text-xs text-gray-600"
                            data-testid={`learnex-review-unmark-${it.id}`}>
                            Un-mark
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Ready-for-quiz confirmation */}
          {!isFaculty && state.all_required_ticked && state.has_quiz && (
            <div className="rounded-lg border-2 p-4 mb-4"
                 style={{
                   borderColor: state.ready_for_quiz ? GOLD : NAVY,
                   background: state.ready_for_quiz
                     ? 'rgba(252,211,89,0.10)' : 'white',
                 }}
                 data-testid="learnex-ready-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={state.ready_for_quiz}
                  disabled={busy}
                  onChange={(e) => confirmReady(e.target.checked)}
                  className="mt-1 w-5 h-5 cursor-pointer"
                  data-testid="learnex-ready-confirm"
                />
                <div>
                  <div className="font-bold" style={{ color: NAVY }}>
                    I am ready to take the quiz.
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    I confirm I have reviewed every required item above and
                    that I'm prepared to be assessed at the medium
                    (30–60<sup>th</sup> percentile) difficulty level.
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Quiz button */}
          {state.has_quiz ? (
            <button
              onClick={() => setShowQuiz(true)}
              disabled={!isFaculty && (!state.all_required_ticked || !state.ready_for_quiz)}
              className="btn-outline text-sm font-bold w-full py-3
                         disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: (isFaculty || (state.all_required_ticked && state.ready_for_quiz))
                  ? GOLD : '#f3f4f6',
                color: (isFaculty || (state.all_required_ticked && state.ready_for_quiz))
                  ? NAVY : '#6b7280',
                borderColor: (isFaculty || (state.all_required_ticked && state.ready_for_quiz))
                  ? GOLD : '#d1d5db',
              }}
              data-testid="learnex-take-quiz-btn"
              title={
                !state.all_required_ticked
                  ? 'Tick every required item to unlock.'
                  : !state.ready_for_quiz
                    ? 'Confirm "I am ready to take the quiz" to unlock.'
                    : 'Take the LearnEx mastery quiz.'
              }>
              {(isFaculty || (state.all_required_ticked && state.ready_for_quiz))
                ? '🎯 Take the LearnEx Quiz'
                : !state.all_required_ticked
                  ? '🔒 Quiz locked — review the required items first'
                  : '🔒 Confirm readiness above to unlock the quiz'}
            </button>
          ) : (
            <div className="text-sm text-gray-500 italic text-center py-2"
                 data-testid="learnex-no-quiz">
              {isFaculty
                ? 'No quiz created yet. Click ✎ Faculty Editor → tab "2. Quiz" to generate one.'
                : 'No quiz available for this topic yet.'}
            </div>
          )}

          {isDeveloper && (
            <div className="text-[10px] text-gray-400 mt-3 italic text-center"
                 data-testid="learnex-developer-claim-footer">
              Pat. 64/084,230 · Cl. 21 (engagement-signal aggregation) ·
              Pat. 64/084,281 · Cl. 14 (human-review routing)
            </div>
          )}
        </>
      )}

      {showEditor && (
        <LearnExEditor
          moduleId={moduleId}
          topicId={topicId}
          onClose={() => setShowEditor(false)}
          onSaved={refresh}
        />
      )}
      {showQuiz && (
        <QuizModal
          moduleId={moduleId}
          topicId={topicId}
          onClose={() => { setShowQuiz(false); refresh({ silent: true }); }}
          onSubmitted={() => refresh({ silent: true })}
        />
      )}
    </div>
  );
};

export default LearnExTab;
